import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Checkbox, Flex, Radio, RadioGroup, Stack, Text } from '@chakra-ui/react';
import PropTypes from 'prop-types';

import FormInput from '../../../../../components/form/FormInput';
import { inputTypes } from '../../../../../components/form/FormInput/util';
import InputLabel from '../../../../../components/form/InputLabel';
import StackedButtonGroup from '../../../../../components/Modal/ModalButtonGroups/StackedButtonGroup';
import { createErrorToast } from '../../../../../redux/modules/app';
import {
  getCompanyEmailTemplates, getCompanySignatureTemplates, getDepartmentEmailTemplates, getDepartmentSignatureTemplates,
  saveEmailTemplate,
  selectCurrentDepartment,
  selectCurrentMessageHtml, selectCurrentSignature,
  selectCurrentSubjectHtml
} from '../../../../../redux/modules/emails';
import { hideModal } from '../../../../../redux/modules/modal';
import { selectUser } from '../../../../../redux/modules/user';
import CompanyDepartmentsDropdown from '../CompanyDepartmentsDropdown';

const SaveEmailTemplateForm = ({ companyId, templateWord, type }) => {
  const dispatch = useDispatch();
  const useFormMethods = useForm();
  const [radioValue, setRadioValue] = useState('company');
  const [defaultCheckbox, setDefaultCheckbox] = useState(false);

  const selectedDepartment = useSelector(selectCurrentDepartment);
  const user = useSelector(selectUser);
  const currentMessageHtml = useSelector(selectCurrentMessageHtml);
  const currentSubjectHtml = useSelector(selectCurrentSubjectHtml);
  const currentSignature = useSelector(selectCurrentSignature);

  const { handleSubmit } = useFormMethods;

  const onSubmit = data => {
    if (radioValue === 'department' && !selectedDepartment?.departmentId) {
      dispatch(createErrorToast(
        'Hold up',
        `If you want to save this ${templateWord} to a department, please pick a department first`
      ));

      return;
    }

    const payload = {
      template_name: data.templateName,
      template_type: type,
      company_id: companyId,
      user_id: user.userId,
      department_id: radioValue === 'department' ? selectedDepartment.departmentId : null,
      default_template: (radioValue === 'company' && defaultCheckbox) ? 'Y' : 'N'
    };

    if (type === 'main_content') {
      payload.template_info = {
        html: currentMessageHtml,
        subjectHtml: currentSubjectHtml
      };
    } else if (type === 'signature') {
      payload.template_info = { html: currentSignature };
    }

    const onSuccess = () => {
      if (type === 'main_content' && radioValue === 'company') {
        dispatch(getCompanyEmailTemplates(companyId));
      }

      if (type === 'signature' && radioValue === 'company') {
        dispatch(getCompanySignatureTemplates(companyId));
      }

      if (type === 'main_content' && radioValue === 'department') {
        dispatch(getDepartmentEmailTemplates(companyId, selectedDepartment.departmentId));
      }

      if (type === 'signature' && radioValue === 'department') {
        dispatch(getDepartmentSignatureTemplates(companyId, selectedDepartment.departmentId));
      }
    };

    dispatch(saveEmailTemplate(payload, onSuccess));

    dispatch(hideModal());
  };

  const onDepartmentSelect = dept => {
    if (type === 'main_content') dispatch(getDepartmentEmailTemplates(companyId, dept.departmentId));
    if (type === 'signature') dispatch(getDepartmentSignatureTemplates(companyId, dept.departmentId));
  };

  const handleCancel = () => {
    dispatch(hideModal());
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormInput
          required
          inputName="templateName"
          labelName={`${templateWord} Name`}
          placeholder={`${templateWord} Name`}
          type={inputTypes.standard}
          useFormMethods={useFormMethods}
          validationRules={{ required: `${templateWord} name is required` }}
        />

        <Box my={8}>
          <InputLabel required labelName={`Save ${templateWord} to:`} />
          <RadioGroup
            value={radioValue}
            onChange={val => setRadioValue(val)}
          >
            <Stack spacing={1}>
              <Radio value="company">Company</Radio>
              <Radio value="department">Department</Radio>
            </Stack>
          </RadioGroup>
        </Box>

        {radioValue === 'company' && (
          <Box my={8}>
            <Checkbox
              isChecked={defaultCheckbox}
              onChange={() => setDefaultCheckbox(!defaultCheckbox)}
            >
              {`Set as default company ${templateWord}?`}
            </Checkbox>
          </Box>
        )}

        {radioValue === 'department' && (
          <Flex flexFlow="row nowrap" justifyContent="space-between" my={8}>
            <Text>
              Select which department:
            </Text>

            <CompanyDepartmentsDropdown onSelect={dept => onDepartmentSelect(dept)} />
          </Flex>
        )}

        <StackedButtonGroup
          dataAnalyticsId="saveEmailTemplate"
          label={`Save ${templateWord}`}
          secondaryClick={handleCancel}
          type="submit"
        />
      </form>
    </Box>
  );
};

SaveEmailTemplateForm.propTypes = {
  companyId: PropTypes.string.isRequired,
  templateWord: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['main_content', 'signature'])
};

export default SaveEmailTemplateForm;
